import type { McpEntityKindDto } from './mcp-component-metadata.schema';
import { resolveMcpResourceComponentUri } from './parse-mcp-component-metadata.util';
import type { AiSignalComponentResolutionInput } from './resolve-ai-signal-component';

export type SignalMcpEntity = {
  serverId?: string;
  kind: McpEntityKindDto;
  toolName?: string;
  promptName?: string;
  resourceUri?: string;
  definitionHash?: string;
};

const MCP_ENTITY_KIND_BY_COMPONENT_TYPE: Record<string, McpEntityKindDto> = {
  MCP_TOOL: 'TOOL',
  MCP_PROMPT: 'PROMPT',
  MCP_RESOURCE: 'RESOURCE',
  MCP_SERVER: 'SERVER',
};

const toMetadataRecord = (value: unknown): Record<string, unknown> => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }

  return {};
};

const readNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;

const readPartialMcpEntity = (metadata: unknown): Record<string, unknown> =>
  toMetadataRecord(toMetadataRecord(metadata).mcpEntity);

const hasMcpEntityNameAnchor = (entity: Record<string, unknown>): boolean =>
  Boolean(
    readNonEmptyString(entity.toolName) ||
      readNonEmptyString(entity.promptName) ||
      readNonEmptyString(entity.resourceUri),
  );

const mcpServerId = (
  component: AiSignalComponentResolutionInput,
): string | undefined =>
  readNonEmptyString(readPartialMcpEntity(component.metadata).serverId);

const uniqueServerId = (
  servers: AiSignalComponentResolutionInput[],
): string | undefined => {
  if (servers.length === 0) {
    return undefined;
  }

  const serverIds = servers.map((component) => mcpServerId(component));

  if (serverIds.some((serverId) => !serverId)) {
    return servers.length === 1 ? serverIds[0] : undefined;
  }

  const unique = [...new Set(serverIds)];

  return unique.length === 1 ? unique[0] : undefined;
};

const findServerIdFromComponents = ({
  components,
  endpointId,
}: {
  components: AiSignalComponentResolutionInput[];
  endpointId?: string | null;
}): string | undefined => {
  const servers = components.filter(
    (component) => component.type === 'MCP_SERVER',
  );

  if (endpointId) {
    const scopedServers = servers.filter(
      (component) => component.endpointId === endpointId,
    );

    if (scopedServers.length > 0) {
      return uniqueServerId(scopedServers);
    }
  }

  return uniqueServerId(servers);
};

export const resolveMcpEntityFromComponent = ({
  component,
  components,
}: {
  component: AiSignalComponentResolutionInput;
  components: AiSignalComponentResolutionInput[];
}): SignalMcpEntity | null => {
  const kind = MCP_ENTITY_KIND_BY_COMPONENT_TYPE[component.type];

  if (!kind) {
    return null;
  }

  const fromMetadata = readPartialMcpEntity(component.metadata);
  const serverId =
    readNonEmptyString(fromMetadata.serverId) ??
    findServerIdFromComponents({
      components,
      endpointId: component.endpointId,
    });
  const definitionHash =
    readNonEmptyString(fromMetadata.definitionHash) ??
    readNonEmptyString(
      toMetadataRecord(toMetadataRecord(component.metadata).declared)
        .definitionHash,
    );

  const toolName =
    kind === 'TOOL'
      ? (readNonEmptyString(fromMetadata.toolName) ?? component.name.trim())
      : undefined;
  const promptName =
    kind === 'PROMPT'
      ? (readNonEmptyString(fromMetadata.promptName) ?? component.name.trim())
      : undefined;
  const resourceUri =
    kind === 'RESOURCE'
      ? resolveMcpResourceComponentUri({
          metadata: component.metadata,
          componentName: component.name,
        })
      : undefined;

  if (kind === 'TOOL' && !toolName) {
    return null;
  }

  if (kind === 'PROMPT' && !promptName) {
    return null;
  }

  if (kind === 'RESOURCE' && !resourceUri) {
    return null;
  }

  return {
    ...(serverId ? { serverId } : {}),
    kind,
    ...(toolName ? { toolName } : {}),
    ...(promptName ? { promptName } : {}),
    ...(resourceUri ? { resourceUri } : {}),
    ...(definitionHash ? { definitionHash } : {}),
  };
};

export const mergeMcpEntityIntoSignalMetadata = ({
  metadata,
  mcpEntity,
}: {
  metadata: unknown;
  mcpEntity: SignalMcpEntity | null;
}): Record<string, unknown> | undefined => {
  const existing = toMetadataRecord(metadata);

  if (!mcpEntity) {
    return Object.keys(existing).length > 0 ? existing : undefined;
  }

  const existingEntity = readPartialMcpEntity(metadata);
  const mergedEntity = hasMcpEntityNameAnchor(existingEntity)
    ? { ...mcpEntity, ...existingEntity }
    : mcpEntity;

  return {
    ...existing,
    mcpEntity: mergedEntity,
  };
};

export const hasSignalMcpEntityNameAnchor = (metadata: unknown): boolean =>
  hasMcpEntityNameAnchor(readPartialMcpEntity(metadata));

const registeredToolName = (
  component: AiSignalComponentResolutionInput,
): string | undefined =>
  readNonEmptyString(readPartialMcpEntity(component.metadata).toolName) ??
  (component.type === 'MCP_TOOL' ? component.name.trim() : undefined);

const registeredPromptName = (
  component: AiSignalComponentResolutionInput,
): string | undefined =>
  readNonEmptyString(readPartialMcpEntity(component.metadata).promptName) ??
  (component.type === 'MCP_PROMPT' ? component.name.trim() : undefined);

export const resolveComponentIdFromMcpEntityAnchor = ({
  metadata,
  components,
}: {
  metadata: unknown;
  components: AiSignalComponentResolutionInput[];
}): string | null => {
  const entity = readPartialMcpEntity(metadata);

  if (!hasMcpEntityNameAnchor(entity)) {
    return null;
  }

  const toolName = readNonEmptyString(entity.toolName);

  if (toolName) {
    const matches = components.filter(
      (component) => registeredToolName(component) === toolName,
    );

    return matches.length === 1 ? matches[0].id : null;
  }

  const promptName = readNonEmptyString(entity.promptName);

  if (promptName) {
    const matches = components.filter(
      (component) => registeredPromptName(component) === promptName,
    );

    return matches.length === 1 ? matches[0].id : null;
  }

  const resourceUri = readNonEmptyString(entity.resourceUri);

  if (resourceUri) {
    const matches = components.filter(
      (component) =>
        component.type === 'MCP_RESOURCE' &&
        resolveMcpResourceComponentUri({
          metadata: component.metadata,
          componentName: component.name,
        }) === resourceUri,
    );

    return matches.length === 1 ? matches[0].id : null;
  }

  return null;
};

export const resolveMcpEntityFromComponentIndex = ({
  assetId,
  components,
}: {
  assetId: string | null;
  components: AiSignalComponentResolutionInput[];
}): SignalMcpEntity | null => {
  if (!assetId) {
    return null;
  }

  const component = components.find((entry) => entry.id === assetId);

  if (!component) {
    return null;
  }

  return resolveMcpEntityFromComponent({ component, components });
};
